// Кнопка «Поддержка» с чатом бота поддержки StudyHub (OXEM-729, OXEM-760).
//
// Сам виджет — кнопку, панель и шторку чата — рисует портал: загрузчик
// `https://iec.exchange/bot-widget/loader.js` с `data-client="studyhub"`
// показывает плавающую кнопку в стиле StudyHub, а по клику открывает фрейм
// портала с ботом `studyhub-support`. Здесь — только встраивание: подключить
// загрузчик один раз и держать его в курсе трёх вещей сервиса:
//
//   • язык интерфейса → `setLang` (бот здоровается и подписывает кнопку на нём);
//   • вошёл ли человек → `setSignedIn` (фрейм тихо входит на портал тем же
//     аккаунтом IEC ID — сервисы живут на `*.iec.exchange`, same-site с порталом);
//   • видна ли кнопка → `setVisible` (правило страниц — `_support-widget-route.ts`,
//     плюс открытая панель репетитора или уведомлений и плашки в том же углу —
//     `stores/support-widget.ts`).
//
// Не подключается:
//   • вне `*.iec.exchange` (dev, превью): фрейм портала пускает себя только на
//     сайты экосистемы (`frame-ancestors`), и тихого входа там нет;
//   • в приложении (Capacitor): origin WebView не same-site с порталом, вход
//     во фрейме не работает, а встраивать чужой веб-чат в магазинную сборку
//     незачем.
//
// Базовый отступ загрузчика резервирует нижнюю навигацию. На десктопе
// (от 1024px) и у гостя этого запаса нет: убираем его правилом размещения.
// Оно работает и при изменении ширины окна, и при входе/выходе в SPA.
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "@/hooks/use-session";
import { IS_CAPACITOR } from "@/lib/platform-detect";
import { useSidePanelStore } from "@/stores/side-panel";
import { useSupportWidgetStore } from "@/stores/support-widget";
import { isSupportWidgetRoute } from "./_support-widget-route";

const LOADER_SRC = "https://iec.exchange/bot-widget/loader.js";
const CLIENT_SLUG = "studyhub";
/** Хосты, на которых портал пускает свой фрейм и умеет тихий вход. */
const ECOSYSTEM_HOST_SUFFIX = ".iec.exchange";
/**
 * Слой кнопки и чата: выше нижнего хрома (`--sh-z-chrome-top`, 40 — мини-плеер,
 * плашки), ниже оверлеев ui-кита (`--sh-z-overlay`, 50) — модалка или шторка
 * сервиса накрывают кнопку, а не наоборот. Загрузчик кладёт фрейм на слой выше.
 */
const Z_INDEX = 45;
/** Воздух между кнопкой и краем экрана (или нижней навигацией). */
const GAP_PX = 16;
/** Высота нижней навигации без safe-area (`h-16` в `bottom-nav.tsx`). */
const BOTTOM_NAV_PX = 64;
const SUPPORT_PARAM = "support";
const SUPPORT_OPEN_VALUE = "open";

interface IecSupportWidgetApi {
	open: () => void;
	close: () => void;
	setLang: (code: string) => void;
	setSignedIn: (flag: boolean) => void;
	setVisible: (flag: boolean) => void;
}

declare global {
	interface Window {
		IecSupportWidget?: IecSupportWidgetApi;
	}
}

function isWidgetHost(): boolean {
	if (IS_CAPACITOR || typeof window === "undefined") return false;
	return window.location.hostname.endsWith(ECOSYSTEM_HOST_SUFFIX);
}

function hasSupportOpenParam(): boolean {
	try {
		return new URL(window.location.href).searchParams.get(SUPPORT_PARAM) === SUPPORT_OPEN_VALUE;
	} catch {
		return false;
	}
}

interface WidgetState {
	lang: string;
	signedIn: boolean;
	visible: boolean;
}

function applyState(api: IecSupportWidgetApi, state: WidgetState): void {
	api.setLang(state.lang);
	api.setSignedIn(state.signedIn);
	api.setVisible(state.visible);
	applyBottomOffset(state.signedIn);
}

function applyBottomOffset(signedIn: boolean): void {
	const host = document.querySelector<HTMLElement>(`[data-iec-support-widget="${CLIENT_SLUG}"]`);
	const root = host?.shadowRoot ?? host;
	if (!root) return;
	const style =
		root.querySelector<HTMLStyleElement>("style[data-studyhub-position]") ??
		document.createElement("style");
	style.dataset.studyhubPosition = "";
	// Смещаем кнопку и её подсказку вместе. Мобильный safe-area загрузчика
	// сохраняется; на планшете навигация тоже есть, хотя виджет уже в panel-режиме.
	style.textContent = `${signedIn ? "@media (min-width: 1024px)" : "@media all"} {
		.fab, .tip { margin-bottom: -${BOTTOM_NAV_PX}px; }
	}`;
	if (!style.isConnected) root.appendChild(style);
}

function injectLoader(state: WidgetState, onLoad: () => void): void {
	const bottom = String(BOTTOM_NAV_PX + GAP_PX);
	const script = document.createElement("script");
	script.async = true;
	script.src = LOADER_SRC;
	script.dataset.client = CLIENT_SLUG;
	script.dataset.lang = state.lang;
	script.dataset.signedIn = state.signedIn ? "1" : "0";
	script.dataset.bottom = bottom;
	script.dataset.bottomMobile = bottom;
	script.dataset.zIndex = String(Z_INDEX);
	script.addEventListener("load", onLoad, { once: true });
	document.body.appendChild(script);
}

/**
 * `?support=open` (push и уведомления об ответе) загрузчик забирает из адреса
 * сам. Но он грузится после проверки сессии, а роутер к тому моменту уже мог
 * переписать адрес: редирект `/settings` → `/settings/profile`, снятие
 * языкового префикса. Первый рендер компонента для этого тоже поздний: начальный
 * переход роутер проходит до него, и параметр к первому кадру уже потерян.
 * Поэтому снимок делается при загрузке модуля: корневой маршрут импортируется
 * до создания роутера. Если до загрузчика параметр не дожил, чат открываем сами.
 */
const SUPPORT_OPEN_AT_START = isWidgetHost() && hasSupportOpenParam();

export function SupportWidget(): null {
	const [enabled] = useState(isWidgetHost);
	const { i18n } = useTranslation();
	const { isAuthenticated, isLoading } = useSession();
	const routeId = useRouterState({
		select: (s): string => s.matches.at(-1)?.routeId ?? "/",
	});
	const sidePanelOpen = useSidePanelStore((s) => s.open);
	const obstructed = useSupportWidgetStore((s) => s.obstructions > 0);

	const state: WidgetState = {
		lang: i18n.resolvedLanguage ?? i18n.language,
		signedIn: isAuthenticated,
		visible: isSupportWidgetRoute(routeId) && !sidePanelOpen && !obstructed,
	};
	// Последнее состояние — для обработчика загрузки: скрипт грузится
	// асинхронно, и пока он летит, человек успевает перейти на другую страницу.
	const stateRef = useRef(state);
	stateRef.current = state;
	const injectedRef = useRef(false);

	useEffect(() => {
		// Ждём ответа о сессии: `data-signed-in` и отступ кнопки читаются
		// загрузчиком один раз.
		if (!enabled || isLoading || injectedRef.current) return;
		injectedRef.current = true;
		const shouldOpen = SUPPORT_OPEN_AT_START && !hasSupportOpenParam();
		injectLoader(stateRef.current, () => {
			const api = window.IecSupportWidget;
			if (!api) return;
			applyState(api, stateRef.current);
			if (shouldOpen && stateRef.current.visible) api.open();
		});
	}, [enabled, isLoading]);

	useEffect(() => {
		const api = window.IecSupportWidget;
		if (!api) return;
		applyState(api, { lang: state.lang, signedIn: state.signedIn, visible: state.visible });
	}, [state.lang, state.signedIn, state.visible]);

	return null;
}
